/**
 * ReactionEditor - Ketcher-based reaction editor
 *
 * Similar to StructureEditor but uses setReaction() for RXN format
 */

import React, { useRef, useState, useEffect, Suspense, lazy } from 'react';
import { createPortal } from 'react-dom';

/**
 * Check if a molfile/rxnfile already has non-zero 2D coordinates (V2000 or V3000).
 */
const molfileHas2DCoords = (molfile: string): boolean => {
  if (!molfile) return false;

  // Helper: check V3000 atom lines for real coordinates
  const checkV3000Block = (block: string): boolean => {
    const lines = block.split('\n');
    for (const line of lines) {
      const trimmed = line.trimStart();
      if (trimmed.startsWith('M  V30') && !trimmed.includes('BEGIN') && !trimmed.includes('END') && !trimmed.includes('COUNTS')) {
        const fields = trimmed.split(/\s+/);
        if (fields.length >= 7) {
          const x = parseFloat(fields[4]);
          const y = parseFloat(fields[5]);
          if (!isNaN(x) && !isNaN(y) && (Math.abs(x) > 0.001 || Math.abs(y) > 0.001)) return true;
        }
      }
    }
    return false;
  };

  // Helper: check V2000 atom block for real coordinates
  const checkV2000Block = (block: string): boolean => {
    const lines = block.split('\n');
    let countsIdx = -1;
    for (let i = 0; i < Math.min(lines.length, 10); i++) {
      if (lines[i].includes('V2000')) { countsIdx = i; break; }
    }
    if (countsIdx < 0) return false;
    const ac = parseInt(lines[countsIdx].substring(0, 3).trim(), 10);
    if (isNaN(ac) || ac === 0) return false;
    for (let i = countsIdx + 1; i < countsIdx + 1 + ac && i < lines.length; i++) {
      if (lines[i].length < 30) continue;
      const x = parseFloat(lines[i].substring(0, 10).trim());
      const y = parseFloat(lines[i].substring(10, 20).trim());
      if (Math.abs(x) > 0.001 || Math.abs(y) > 0.001) return true;
    }
    return false;
  };

  // For RXN files, check each embedded $MOL block
  if (molfile.trim().startsWith('$RXN')) {
    const molBlocks = molfile.split('$MOL');
    for (let b = 1; b < molBlocks.length; b++) {
      const block = molBlocks[b];
      if (block.includes('V3000') || block.includes('V30 BEGIN')) {
        if (checkV3000Block(block)) return true;
      } else if (block.includes('V2000')) {
        if (checkV2000Block(block)) return true;
      }
    }
    return false;
  }

  // Standalone V3000 molfile
  if (molfile.includes('V3000') || molfile.includes('V30 BEGIN')) {
    return checkV3000Block(molfile);
  }

  // Standalone V2000 molfile
  if (molfile.includes('V2000')) {
    return checkV2000Block(molfile);
  }

  return false;
};

// Lazy load Editor with RemoteStructServiceProvider
const KetcherEditor = lazy(() =>
  Promise.all([
    import('ketcher-react'),
    import('ketcher-core'),
    import('ketcher-react/dist/index.css'),
  ]).then(([ketcherReact, ketcherCore]) => ({
    default: function LazyKetcherEditor({ onInit }: { onInit: (ketcher: any) => void }) {
      const { Editor } = ketcherReact;
      const { RemoteStructServiceProvider } = ketcherCore;
      const structServiceProvider = new RemoteStructServiceProvider('/api/v2/');

      return (
        <>
          {/* Keep Ketcher's portalled MUI popovers just above the modal overlay (z-9000),
              not at a global 100000 that floats them detached over the page. */}
          <style>{`.MuiPopover-root { z-index: 9001 !important; }`}</style>
          <Editor
            staticResourcesUrl=""
            structServiceProvider={structServiceProvider}
            onInit={onInit}
            errorHandler={(message) => console.error('[Ketcher Error]', message)}
          />
        </>
      );
    },
  }))
);

interface ReactionEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (rxnfile: string, smiles: string) => void;
  initialReaction?: string; // RXN format or reaction SMILES
  title?: string;
}

export const ReactionEditorModal: React.FC<ReactionEditorModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialReaction = '',
  title = 'Reaction Editor'
}) => {
  const ketcherRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [reactionLoading, setReactionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editorKey, setEditorKey] = useState(0);

  const handleKetcherInit = async (ketcher: any) => {
    console.log('[ReactionEditor] onInit called');
    ketcherRef.current = ketcher;

    // Monkey-patch: suppress visual "swoosh" during server transforms.
    const patchRenderToSuppressFlash = (editor: any) => {
      try {
        const render = editor?.render;
        if (!render || render.__patchedNoFlash) return;
        render.__patchedNoFlash = true;
        const origSetMolecule = render.setMolecule.bind(render);
        render.setMolecule = function (struct: any, forceUpdateWithTimeout?: boolean) {
          const svg = render.paper?.canvas;
          if (svg) svg.style.visibility = 'hidden';
          origSetMolecule(struct, forceUpdateWithTimeout);
          requestAnimationFrame(() => { if (svg) svg.style.visibility = ''; });
        };
      } catch (e) {
        console.warn('[ReactionEditor] Failed to patch render:', e);
      }
    };
    if (ketcher.editor) {
      patchRenderToSuppressFlash(ketcher.editor);
    } else {
      (async () => {
        for (let i = 0; i < 50; i++) {
          if (ketcher.editor) { patchRenderToSuppressFlash(ketcher.editor); break; }
          await new Promise(r => setTimeout(r, 100));
        }
      })();
    }

    setLoading(false);

    if (initialReaction) {
      setReactionLoading(true);

      const waitForEditor = async (maxAttempts = 50): Promise<boolean> => {
        for (let i = 0; i < maxAttempts; i++) {
          if (ketcher.editor) {
            console.log(`[ReactionEditor] editor ready after ${i} attempts`);
            return true;
          }
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        return false;
      };

      const loadReaction = async () => {
        try {
          const editorReady = await waitForEditor();
          if (!editorReady) {
            throw new Error('Editor failed to initialize');
          }

          const reactionSource = initialReaction.trim();
          // Detect if it's RXN format or reaction SMILES
          const isRxnFormat = reactionSource.startsWith('$RXN');

          if (isRxnFormat) {
            // Use setMolecule for RXN format - Ketcher handles it
            console.log('[ReactionEditor] Loading RXN format');
            await ketcher.setMolecule(reactionSource);
          } else {
            // Lay out reaction SMILES through the same Indigo service Ketcher uses.
            // Passing mapped reaction SMILES straight to setMolecule can resolve
            // without drawing anything, leaving a misleading blank canvas.
            console.log('[ReactionEditor] Laying out reaction SMILES:', reactionSource);
            const layoutResponse = await fetch('/api/v2/indigo/layout', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ struct: reactionSource }),
            });
            if (!layoutResponse.ok) {
              throw new Error(`Reaction layout failed (${layoutResponse.status})`);
            }
            const layoutResult = await layoutResponse.json();
            if (!layoutResult?.struct) {
              throw new Error('Reaction layout returned no structure');
            }
            await ketcher.setMolecule(layoutResult.struct);
          }

          setReactionLoading(false);
        } catch (err) {
          console.error('[ReactionEditor] Failed to load reaction:', err);
          setReactionLoading(false);
          setError('Failed to load reaction.');
        }
      };

      loadReaction();
    }
  };

  useEffect(() => {
    if (isOpen) {
      setEditorKey(prev => prev + 1);
      setLoading(true);
      setReactionLoading(false);
      setError(null);
    }
  }, [isOpen, initialReaction]);

  const handleClose = () => {
    onClose();
  };

  const handleSave = async () => {
    if (ketcherRef.current && onSave) {
      try {
        // Get RXN format for reactions
        let rxnfile = '';
        let smiles = '';

        try {
          // Try to get as RXN first
          rxnfile = await ketcherRef.current.getRxn();
          console.log('[ReactionEditor] Got RXN, length:', rxnfile?.length);
        } catch (e) {
          // Fall back to molfile if not a reaction
          rxnfile = await ketcherRef.current.getMolfile();
          console.log('[ReactionEditor] Got molfile instead, length:', rxnfile?.length);
        }

        try {
          smiles = await ketcherRef.current.getSmiles();
        } catch (e) {
          console.warn('[ReactionEditor] Could not get SMILES');
        }

        onSave(rxnfile, smiles);
        onClose();
      } catch (e) {
        console.error('[ReactionEditor] Error saving:', e);
      }
    }
  };

  if (!isOpen) return null;

  // Portal to <body> so the overlay escapes the host page's stacking context
  // (e.g. EditorPage's `relative z-10` wrapper) and its z-[9000] competes at the
  // document root — above the z-40 LiquidGlassHeader. Matches the MUI-popover
  // intent (.MuiPopover-root is already body-portaled at z-9001).
  return createPortal(
    <div
      className="fixed inset-0 z-[9000] flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-[90vw] h-[85vh] max-w-6xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
          >
            &times;
          </button>
        </div>

        {/* Editor */}
        <div className="flex-1 overflow-hidden relative [&>div]:h-full">
          {error && (
            <div className="absolute top-2 left-2 right-2 z-10 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {error}
            </div>
          )}

          {reactionLoading && (
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10 bg-white bg-opacity-90 px-6 py-3 rounded-lg shadow text-gray-600">
              Loading reaction...
            </div>
          )}

          <Suspense
            fallback={
              <div className="flex items-center justify-center h-full">
                <div className="text-gray-500">Loading reaction editor...</div>
              </div>
            }
          >
            <KetcherEditor key={editorKey} onInit={handleKetcherInit} />
          </Suspense>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
          >
            Close
          </button>
          {onSave && (
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
            >
              Save Reaction
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReactionEditorModal;
